import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";

const phoneNumber = "250791099925";

const options = [
  {
    label: "Spare Parts Inquiry",
    message: "Hello, I want to ask about Hyundai or Kia spare parts",
    emoji: "🔧",
  },
  {
    label: "Schedule a Service",
    message: "Hello, I want to schedule a service with your team",
    emoji: "📅",
  },
  {
    label: "Vehicle Sales",
    message: "Hello, I want to know about vehicle sales",
    emoji: "🚗",
  },
];

const openWhatsApp = (message) => {
  const encodedMessage = encodeURIComponent(message);
  window.open(
    `https://wa.me/${phoneNumber}?text=${encodedMessage}`,
    "_blank",
    "noopener,noreferrer"
  );
};

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldVibrate, setShouldVibrate] = useState(false);

  /* Trigger vibration pulse every 8 seconds when panel is closed */
  useEffect(() => {
    if (isOpen) return;
    const initial = setTimeout(() => setShouldVibrate(true), 2000);
    const interval = setInterval(() => {
      setShouldVibrate(true);
      setTimeout(() => setShouldVibrate(false), 900);
    }, 8000);
    return () => {
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [isOpen]);

  useEffect(() => {
    if (shouldVibrate) {
      const timer = setTimeout(() => setShouldVibrate(false), 900);
      return () => clearTimeout(timer);
    }
  }, [shouldVibrate]);

  return (
    <Widget>
      {isOpen && (
        <Panel>
          {/* Header */}
          <PanelHeader>
            <AvatarWrap>
              <WhatsAppSVG size={22} />
              <OnlineDot />
            </AvatarWrap>
            <HeaderInfo>
              <BrandName>Felicie Auto</BrandName>
              <StatusRow>
                <StatusDot />
                <StatusText>Typically replies instantly</StatusText>
              </StatusRow>
            </HeaderInfo>
            <CloseBtn
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close WhatsApp chat"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </PanelHeader>

          {/* Bubble intro */}
          <BubbleArea>
            <Bubble>
              👋 Hi there! How can we help you today?
            </Bubble>
          </BubbleArea>

          {/* Options */}
          <OptionsArea>
            <OptionsLabel>Choose a topic</OptionsLabel>
            {options.map((opt, i) => (
              <OptionBtn
                key={opt.label}
                type="button"
                onClick={() => openWhatsApp(opt.message)}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <OptionEmoji>{opt.emoji}</OptionEmoji>
                <OptionLabel>{opt.label}</OptionLabel>
                <ArrowIcon viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </ArrowIcon>
              </OptionBtn>
            ))}
          </OptionsArea>

          <FooterNote>🔒 Your messages are end-to-end encrypted</FooterNote>
        </Panel>
      )}

      {/* Floating button */}
      <FloatWrap>
        {!isOpen && <RippleRing />}
        {!isOpen && <RippleRing $delay="0.6s" />}
        <FloatBtn
          type="button"
          $vibrate={shouldVibrate && !isOpen}
          $open={isOpen}
          onClick={() => {
            setIsOpen((v) => !v);
            setShouldVibrate(false);
          }}
          aria-label={isOpen ? "Close WhatsApp chat" : "Open WhatsApp chat"}
        >
          {isOpen ? (
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <WhatsAppSVG size={28} />
          )}
        </FloatBtn>
        {!isOpen && <NotifBadge aria-label="New message">1</NotifBadge>}
      </FloatWrap>
    </Widget>
  );
};

/* ── WhatsApp icon SVG ── */
const WhatsAppSVG = ({ size = 28 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} fill="#fff" aria-hidden="true">
    <path d="M16.03 3.2c-7.02 0-12.72 5.6-12.72 12.5 0 2.2.6 4.36 1.72 6.25L3.2 28.8l7.08-1.78a12.9 12.9 0 0 0 5.75 1.36c7.02 0 12.72-5.6 12.72-12.5S23.05 3.2 16.03 3.2Zm0 22.98c-1.8 0-3.56-.47-5.1-1.35l-.37-.22-4.2 1.06 1.1-4.02-.25-.4a10.14 10.14 0 0 1-1.62-5.55c0-5.69 4.68-10.3 10.44-10.3 5.75 0 10.43 4.61 10.43 10.3 0 5.68-4.68 10.48-10.43 10.48Zm5.72-7.7c-.31-.16-1.85-.9-2.14-1-.29-.1-.5-.15-.7.16-.2.3-.8.98-.98 1.18-.18.2-.36.22-.67.07-.31-.16-1.32-.48-2.52-1.53-.93-.82-1.56-1.84-1.74-2.14-.18-.31-.02-.48.14-.64.14-.14.31-.36.47-.54.16-.18.2-.31.31-.52.1-.2.05-.38-.03-.54-.07-.16-.7-1.67-.96-2.29-.25-.6-.51-.52-.7-.53h-.6c-.2 0-.54.08-.82.38-.29.31-1.08 1.04-1.08 2.54s1.1 2.95 1.25 3.15c.16.2 2.16 3.25 5.23 4.56.73.31 1.3.5 1.74.64.73.23 1.4.2 1.93.12.59-.09 1.85-.75 2.11-1.47.26-.72.26-1.34.18-1.47-.08-.13-.29-.2-.6-.36Z" />
  </svg>
);

export default WhatsAppButton;

/* ═══════════════════════════════════════
   KEYFRAMES
═══════════════════════════════════════ */

const ripple = keyframes`
  0%   { transform: scale(1);   opacity: 0.55; }
  100% { transform: scale(2.2); opacity: 0; }
`;

const vibrate = keyframes`
  0%   { transform: rotate(0deg)   scale(1); }
  10%  { transform: rotate(-12deg) scale(1.08); }
  20%  { transform: rotate(12deg)  scale(1.08); }
  30%  { transform: rotate(-10deg) scale(1.06); }
  40%  { transform: rotate(10deg)  scale(1.06); }
  50%  { transform: rotate(-6deg)  scale(1.04); }
  60%  { transform: rotate(6deg)   scale(1.04); }
  70%  { transform: rotate(-3deg)  scale(1.02); }
  80%  { transform: rotate(3deg)   scale(1.02); }
  90%  { transform: rotate(-1deg)  scale(1); }
  100% { transform: rotate(0deg)   scale(1); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)     scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const badgePop = keyframes`
  0%   { transform: scale(0); }
  70%  { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

/* ═══════════════════════════════════════
   STYLES
═══════════════════════════════════════ */

const Widget = styled.div`
  position: fixed;
  left: 24px;
  bottom: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;

  @media (max-width: 480px) {
    left: 12px;
    bottom: 14px;
  }
`;

/* ── Panel ── */

const Panel = styled.div`
  width: 340px;
  background: #fff;
  border-radius: 18px;
  box-shadow:
    0 4px 6px rgba(0,0,0,0.04),
    0 24px 60px rgba(7, 17, 22, 0.22);
  overflow: hidden;
  animation: ${slideUp} 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) both;

  @media (max-width: 480px) {
    width: calc(100vw - 24px);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #00a651 0%, #00c853 100%);
`;

const AvatarWrap = styled.div`
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OnlineDot = styled.span`
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 11px;
  height: 11px;
  background: #a5f3c0;
  border: 2px solid #00c853;
  border-radius: 50%;
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const BrandName = styled.p`
  font-size: 16px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 3px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  background: #a5f3c0;
  border-radius: 50%;
  flex-shrink: 0;
`;

const StatusText = styled.span`
  font-size: 12px;
  color: rgba(255,255,255,0.85);
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.15);
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255,255,255,0.28);
  }
`;

/* ── Bubble ── */

const BubbleArea = styled.div`
  padding: 18px 18px 0;
  background: #f0fdf4;
`;

const Bubble = styled.div`
  display: inline-block;
  background: #fff;
  border: 1px solid #d1fae5;
  border-radius: 0 14px 14px 14px;
  padding: 12px 16px;
  font-size: 15px;
  color: #1f2937;
  line-height: 1.5;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  animation: ${fadeIn} 0.3s 0.1s ease both;
`;

/* ── Options ── */

const OptionsArea = styled.div`
  padding: 14px 14px 10px;
  background: #f0fdf4;
  display: grid;
  gap: 8px;
`;

const OptionsLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #6b7280;
  padding: 0 4px;
  margin-bottom: 2px;
`;

const OptionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #d1fae5;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  animation: ${fadeIn} 0.25s ease both;

  &:hover {
    background: #f0fdf4;
    border-color: #6ee7b7;
    transform: translateX(3px);
    box-shadow: 0 4px 16px rgba(0, 200, 83, 0.12);
  }

  &:active {
    transform: translateX(1px) scale(0.99);
  }
`;

const OptionEmoji = styled.span`
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
`;

const OptionLabel = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.35;
`;

const ArrowIcon = styled.svg`
  width: 16px;
  height: 16px;
  color: #00c853;
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform 0.15s ease, opacity 0.15s ease;

  ${OptionBtn}:hover & {
    transform: translateX(3px);
    opacity: 1;
  }
`;

const FooterNote = styled.p`
  padding: 10px 18px 14px;
  background: #f0fdf4;
  font-size: 11px;
  color: #9ca3af;
  text-align: center;
  border-top: 1px solid #d1fae5;
`;

/* ── Floating button ── */

const FloatWrap = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
`;

const RippleRing = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 200, 83, 0.35);
  animation: ${ripple} 2.4s ${(p) => p.$delay || "0s"} ease-out infinite;
  pointer-events: none;
`;

const FloatBtn = styled.button`
  position: relative;
  z-index: 1;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #00c853, #00a651);
  border-radius: 50%;
  box-shadow:
    0 4px 12px rgba(0, 200, 83, 0.3),
    0 16px 40px rgba(0, 200, 83, 0.25);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  ${(p) =>
    p.$vibrate &&
    css`
      animation: ${vibrate} 0.8s ease;
    `}

  ${(p) =>
    p.$open &&
    `
    background: linear-gradient(145deg, #374151, #1f2937);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2), 0 16px 40px rgba(0,0,0,0.15);
  `}

  &:hover {
    transform: scale(1.1);
    box-shadow:
      0 6px 16px rgba(0, 200, 83, 0.36),
      0 20px 50px rgba(0, 200, 83, 0.3);
  }

  &:active {
    transform: scale(0.96);
  }
`;

const NotifBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 20px;
  height: 20px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  border-radius: 50%;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${badgePop} 0.4s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  pointer-events: none;
`;